import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import {
    ActionMenu,
    Breaker,
    Button,
    ButtonLoader,
    Dropdown,
    FileUploadBox,
    Flexbox,
    InputField,
    ToggleButton,
    WrapperBox,
} from "../../Theme/GlobalStyles";
import { globalTheme } from "../../Theme/Theme";
import { Text } from "../../Theme/Typography";
import {
    AccordianIcon,
    AddIcon,
    Clock,
    GreenCheckFilled,
    InfoIcon,
} from "../../Theme/Icons";
import {
    clearCoworkSliceStates,
    coworkData,
    getAllAmenities,
    getAllTowers,
    getEquipmentList,
    postRoom,
    postTower,
    postUpdateRoom,
} from "../../../redux/cowork/coworkSlice";
import { MODULES } from "../../../redux/appModule/appModuleSlice";
import useMultiUpload from "../../../utils/CustomHooks/useMultiUpload";
import EditAmenityPopup from "../../Components/Popup/EditAmenityPopup";
import AddEquipmentPopup from "../../Components/Popup/AddEquipmentPopup";

const WEEK_DAYS = [
    { key: "sun", label: "S" },
    { key: "mon", label: "M" },
    { key: "tue", label: "T" },
    { key: "wed", label: "W" },
    { key: "thu", label: "T" },
    { key: "fri", label: "F" },
    { key: "sat", label: "S" },
];

// DB TIME columns come back as "HH:MM:SS" (e.g. "09:00:00"), but the dropdown
// options are keyed on "HH:MM" ("09:00"). Trim to "HH:MM" so the stored value
// matches an option and the picker shows it instead of rendering blank.
const toHHMM = (t) => (typeof t === "string" ? t.slice(0, 5) : "");

const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`);

// Generates time slot options across the full day in `stepMinutes` increments.
// Same pattern as DashboardUser's generateTimeSlots, but for a single time
// picker (one value per option, not a range).
const buildTimeOptions = (stepMinutes = 30) => {
    const options = [];
    let h = 0;
    let m = 0;
    while (h < 24) {
        const value = `${pad2(h)}:${pad2(m)}`;
        const period = h >= 12 ? "PM" : "AM";
        const hr12 = h % 12 || 12;
        const label = `${hr12}:${pad2(m)} ${period}`;
        options.push({ value, label });
        m += stepMinutes;
        if (m >= 60) {
            h += Math.floor(m / 60);
            m = m % 60;
        }
    }
    return options;
};

const TIME_OPTIONS = buildTimeOptions(30);

const EditMeetingRoom = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {
        isCoworkSliceFetching,
        isCoworkSliceSuccess,
        isCoworkSliceError,
        coworkSliceSuccessMessage,
        coworkSliceErrorMessage,
        roomToBeUpdatedDetails,
        towersList,
        amenitiesList,
        equipmentList,
    } = useSelector(coworkData);
    const selectedModule = useSelector((state) => state.appModule.selectedModule);
    const isLab = selectedModule === MODULES.MAKERSLAB;

    const rolesCookie = Cookies.get("roles");
    const rolesArray = rolesCookie ? JSON.parse(rolesCookie) : [];
    const userRoles = rolesArray;

    const [roomData, setRoomData] = useState({
        roomName: "",
        images: [],
        tower: "",
        capacity: 0,
        status: "",
        availableFrom: "",
        availableTo: "",
        availableDay: [],
        slotDuration: 30,
    });

    const [uploadingField, setUploadingField] = useState(null);
    // Tracks a save in flight (add/update room) so we can show the button loader
    // and navigate back to the list only after a save — not after a tower create.
    const [isSaving, setIsSaving] = useState(false);
    const imageRef = useRef(null);

    // Per-field validation errors (shown inline under each field) + refs so we can
    // scroll the first invalid field into view instead of relying on a toast.
    const [errors, setErrors] = useState({});
    // DOM refs for scroll-to-first-error. Times share one ref (they sit on one
    // row, and the Dropdown forwards its ref to react-select, not a DOM node).
    const fieldRefs = {
        roomName: useRef(null),
        capacity: useRef(null),
        tower: useRef(null),
        images: useRef(null),
        availability: useRef(null),
    };

    const [amenityOptions, setAmenityOptions] = useState([]);
    const [selectedAmenities, setSelectedAmenities] = useState([]);
    const [addAmenityPopupOpen, setAddAmenityPopupOpen] = useState(false);

    // Lab equipment (Makers Lab only) — mirrors the amenities picker.
    const [equipmentOptions, setEquipmentOptions] = useState([]);
    const [selectedEquipment, setSelectedEquipment] = useState([]);
    const [addEquipmentPopupOpen, setAddEquipmentPopupOpen] = useState(false);

    const [typedTower, setTypedTower] = useState("");
    const pendingTowerNameRef = useRef(null);

    const {
        isFileUploading,
        fileUploadError,
        fileUploadSuccess,
        uploadedFileData,
        uploadFile,
    } = useMultiUpload();

    const handleRoomDataChange = (key, value) => {
        // Clear the inline error for whatever field is being edited.
        const errKey = key === "roomImages" ? "images" : key === "availableDay" ? "availableDay" : key;
        setErrors((prev) => (prev[errKey] ? { ...prev, [errKey]: "" } : prev));
        if (key === "roomName") {
            setRoomData({ ...roomData, roomName: value });
        }
        if (key === "roomImages") {
            setRoomData({ ...roomData, images: [...roomData.images, value] });
        }
        if (key === "tower") {
            setRoomData({ ...roomData, tower: value });
        }
        if (key === "capacity") {
            setRoomData({ ...roomData, capacity: value });
        }
        if (key === "status") {
            setRoomData({ ...roomData, status: value });
        }
        if (key === "availableFrom") {
            setRoomData({ ...roomData, availableFrom: value });
        }
        if (key === "availableTo") {
            setRoomData({ ...roomData, availableTo: value });
        }
        if (key === "availableDay") {
            setRoomData({
                ...roomData,
                availableDay: roomData.availableDay.includes(value)
                    ? roomData.availableDay.filter((d) => d !== value)
                    : [...roomData.availableDay, value],
            });
        }
        if (key === "slotDuration") {
            setRoomData({ ...roomData, slotDuration: value });
        }
    };

    useEffect(() => {
        dispatch(getAllTowers());
        dispatch(getAllAmenities());
        if (isLab) dispatch(getEquipmentList());
    }, [dispatch, isLab]);

    useEffect(() => {
        setAmenityOptions(
            (amenitiesList || []).map((amenity) => ({
                name: amenity.name,
                value: amenity.id,
            }))
        );
    }, [amenitiesList]);

    useEffect(() => {
        setEquipmentOptions(
            (equipmentList || []).map((equipment) => ({
                name: equipment.name,
                value: equipment.id,
            }))
        );
    }, [equipmentList]);

    useEffect(() => {
        if (!pendingTowerNameRef.current) return;
        const created = (towersList || []).find(
            (t) =>
                t?.tower?.toLowerCase().trim() ===
                pendingTowerNameRef.current
        );
        if (created) {
            setRoomData((prev) => ({ ...prev, tower: created.id }));
            pendingTowerNameRef.current = null;
        }
    }, [towersList]);

    const handleTowerBlur = async () => {
        const trimmed = typedTower?.trim();
        if (!trimmed) return;
        const existing = (towersList || []).find(
            (t) =>
                t?.tower?.toLowerCase().trim() === trimmed.toLowerCase()
        );
        if (existing) {
            handleRoomDataChange("tower", existing.id);
            setTypedTower("");
            return;
        }
        pendingTowerNameRef.current = trimmed.toLowerCase();
        await dispatch(postTower({ towerName: trimmed }));
        dispatch(getAllTowers());
        setTypedTower("");
    };

    useEffect(() => {
        if (!roomToBeUpdatedDetails) return;
        setRoomData((prev) => ({
            ...prev,
            roomName: roomToBeUpdatedDetails?.room_name || "",
            images: roomToBeUpdatedDetails?.roomImages?.length
                ? roomToBeUpdatedDetails.roomImages
                : roomToBeUpdatedDetails?.room_thumbnail
                ? [roomToBeUpdatedDetails.room_thumbnail]
                : [],
            // Store the tower ID (the ActionMenu options are keyed on id), so the
            // saved tower pre-selects. Fall back to the raw tower field only if no
            // id is present.
            tower:
                roomToBeUpdatedDetails?.tower_id ||
                roomToBeUpdatedDetails?.tower ||
                "",
            capacity: roomToBeUpdatedDetails?.capacity || 0,
            status: roomToBeUpdatedDetails?.availability ? "active" : "maintenance",
            availableFrom: toHHMM(
                roomToBeUpdatedDetails?.availableFrom ||
                roomToBeUpdatedDetails?.available_from ||
                ""
            ),
            availableTo: toHHMM(
                roomToBeUpdatedDetails?.availableTo ||
                roomToBeUpdatedDetails?.available_to ||
                ""
            ),
            availableDay:
                roomToBeUpdatedDetails?.availableDays ||
                roomToBeUpdatedDetails?.available_days ||
                [],
            slotDuration: roomToBeUpdatedDetails?.slot_duration || 30,
        }));
        setSelectedAmenities(
            (roomToBeUpdatedDetails?.amenities || []).map((amenity) => ({
                name: amenity.name,
                value: amenity.id,
            }))
        );
        setSelectedEquipment(
            (roomToBeUpdatedDetails?.equipments || []).map((equipment) => ({
                name: equipment.name,
                value: equipment.id,
            }))
        );
    }, [roomToBeUpdatedDetails]);

    const onFileSelect = async (files) => {
        setUploadingField("image");
        await uploadFile(files, "image");
    };

    useEffect(() => {
        if (fileUploadSuccess && !isFileUploading && uploadedFileData?.length) {
            const newUrls = uploadedFileData.map((f) => f.url);
            setRoomData((prev) => ({
                ...prev,
                images: [...prev.images, ...newUrls],
            }));
            setUploadingField(null);
        }
    }, [isFileUploading, fileUploadSuccess, uploadedFileData]);

    useEffect(() => {
        if (fileUploadError) {
            // Fixed toastId: react-toastify then guarantees only one upload-error
            // toast exists at a time, so a re-fire updates it in place instead of
            // stacking a second one on top (which rendered as unreadable
            // overlapping text). Longer autoClose — these messages need reading.
            toast.error(fileUploadError, {
                toastId: "file-upload-error",
                autoClose: 8000,
            });
        }
    }, [fileUploadError]);

    useEffect(() => {
        if (isCoworkSliceSuccess && coworkSliceSuccessMessage) {
            toast.success(coworkSliceSuccessMessage, {
                autoClose: 3000,
            });
            dispatch(clearCoworkSliceStates());
            // Only a room save should send the admin back to the list — a tower
            // create also flips this success flag but must keep them on the form.
            if (isSaving) {
                setIsSaving(false);
                navigate("/MeetingRooms");
            }
        }
        if (isCoworkSliceError && coworkSliceErrorMessage) {
            toast.error(coworkSliceErrorMessage, {
                autoClose: 3000,
            });
            dispatch(clearCoworkSliceStates());
            if (isSaving) setIsSaving(false);
        }
    }, [
        isCoworkSliceSuccess,
        isCoworkSliceError,
        coworkSliceSuccessMessage,
        coworkSliceErrorMessage,
        isSaving,
        navigate,
        dispatch,
    ]);

    const removeImage = (index) => {
        setRoomData({
            ...roomData,
            images: roomData.images.filter((_, i) => i !== index),
        });
    };

    // Validate all fields, returning an { field: message } map. Times are optional
    // (a room with none defaults to a full-day / all-week window when booking), but
    // if used they must be a valid pair with Till after From.
    const validateRoom = () => {
        const next = {};
        if (!roomData.roomName?.trim()) next.roomName = "Room name is required.";
        if (!roomData.capacity || Number(roomData.capacity) <= 0)
            next.capacity = "Enter a capacity greater than 0.";
        if (!roomData.tower) next.tower = "Please select a tower.";
        if (!roomData.images?.length) next.images = "Add at least one room photo.";

        const from = roomData.availableFrom;
        const to = roomData.availableTo;
        if (from && !to) next.availableTo = "Select an end time (or clear the start time).";
        if (!from && to) next.availableFrom = "Select a start time (or clear the end time).";
        if (from && to && to <= from)
            next.availableTo = "End time must be after the start time.";

        return next;
    };

    // First-invalid order → which DOM ref to scroll to. Both time errors map to
    // the shared availability row.
    const FIELD_ORDER = ["roomName", "capacity", "tower", "images", "availableFrom", "availableTo"];
    const refForField = (key) =>
        key === "availableFrom" || key === "availableTo" ? fieldRefs.availability : fieldRefs[key];

    const onSave = () => {
        const next = validateRoom();
        setErrors(next);
        if (Object.keys(next).length) {
            // Scroll the first invalid field into view instead of a toast.
            const firstKey = FIELD_ORDER.find((k) => next[k]);
            const node = firstKey && refForField(firstKey)?.current;
            if (node?.scrollIntoView) {
                node.scrollIntoView({ behavior: "smooth", block: "center" });
            }
            return;
        }
        setIsSaving(true);
        // roomData.tower holds the tower ID. The update endpoint wants both the
        // tower name (`tower`) and its id (`tower_id`), so resolve the object from
        // the towers list (this is the old UpdateRoomPopup behaviour the new form
        // dropped — sending the id alone landed in the name column).
        const selectedTower = (towersList || []).find(
            (t) => t.id === roomData.tower
        );
        const equipmentIds = selectedEquipment.map((e) => e.value);
        if(roomToBeUpdatedDetails){
            dispatch(
                postUpdateRoom({
                    id: roomToBeUpdatedDetails?.id,
                    roomName: roomData.roomName,
                    roomCapacity: roomData.capacity,
                    tower: selectedTower?.tower ?? roomData.tower,
                    tower_id: selectedTower?.id ?? roomData.tower,
                    availability: roomData.status === "maintenance" ? 0 : 1,
                    room_thumbnail: roomData.images[0],
                    roomImages: roomData.images,
                    availableDays: roomData.availableDay,
                    availableFrom: roomData.availableFrom,
                    availableTo: roomData.availableTo,
                    category: roomToBeUpdatedDetails?.category || "",
                    amenities: selectedAmenities.map((amenity) => amenity.value),
                    // Preserve the room's equipment on update — omitting it wiped
                    // it, since the update endpoint replaces with `equipments || []`.
                    equipments: equipmentIds,
                })
            );
        } else {
            const isClassAdmin = selectedModule === MODULES.EDUSPACE;
            const isBmb = selectedModule === MODULES.BLOOMBERG;
            dispatch(
                postRoom({
                    roomName: roomData.roomName,
                    roomCapacity: roomData.capacity,
                    tower: roomData.tower,
                    availability: roomData.status === "maintenance" ? 0 : 1,
                    room_thumbnail: roomData.images[0],
                    roomImages: roomData.images,
                    availableDays: roomData.availableDay,
                    availableFrom: roomData.availableFrom,
                    availableTo: roomData.availableTo,
                    isClassAdmin: isClassAdmin ? 1 : 0,
                    isLab: isLab ? 1 : 0,
                    isBmb: isBmb ? 1 : 0,
                    organizationId: isLab
                        ? "52c3b93c-59ba-44e7-8e99-bd5fc889b68f"
                        : null,
                    category: "",
                    amenities: selectedAmenities.map((amenity) => amenity.value),
                    equipments: equipmentIds,
                })
            );
        }
    };

    return (
        <>
            <EditAmenityPopup
                isActive={addAmenityPopupOpen}
                togglePopup={setAddAmenityPopupOpen}
            />
            <AddEquipmentPopup
                isActive={addEquipmentPopupOpen}
                togglePopup={setAddEquipmentPopupOpen}
            />
            <Flexbox column gap={16} padding="20px">
                <WrapperBox
                    borderRadius={30}
                    borderColor={globalTheme.colors.nuetral2}
                    overflow={'visible'}
                >
                    <Flexbox spaceBW alignCenter>
                        <Text
                            font16
                            inter
                            medium
                            black4
                            maxWidth="max-content"
                        >
                            Basic Information
                        </Text>
                        <AccordianIcon />
                    </Flexbox>
                    <Breaker
                        marginTop={16}
                        marginBottom={16}
                        borderColor={globalTheme.colors.nuetral2}
                    />
                    <Flexbox gap={16} alignStart className="respFormRow">
                        <InputField
                            ref={fieldRefs.roomName}
                            label="Room Name"
                            isImp
                            placeholder="Laser Cutters, Robotics, and DIY Zone"
                            value={roomData.roomName}
                            error={errors.roomName}
                            onChange={(e) =>
                                handleRoomDataChange("roomName", e.target.value)
                            }
                        />
                        <InputField
                            ref={fieldRefs.capacity}
                            label="Capacity"
                            isImp
                            type="number"
                            placeholder="Enter Capacity"
                            value={roomData.capacity}
                            error={errors.capacity}
                            onChange={(e) =>
                                handleRoomDataChange(
                                    "capacity",
                                    Number(e.target.value)
                                )
                            }
                        />
                        {/* <Dropdown
                            label="Tower"
                            isImp
                            placeholder="Select or type a tower"
                            value={roomData.tower}
                            options={(towersList || []).map((tower) => ({
                                value: tower.id,
                                label: tower.tower,
                            }))}
                            onChange={(e) =>
                                handleRoomDataChange("tower", e.value)
                            }
                            onInputChange={(inputValue) =>
                                setTypedTower(inputValue || "")
                            }
                            onBlur={handleTowerBlur}
                        /> */}
                        <Flexbox ref={fieldRefs.tower} column gap={8} maxWidth="max-content" alignStart>
                            <Text font14 inter medium black4>
                                Tower <span style={{ color: globalTheme.colors.red }}>*</span>
                            </Text>
                            <WrapperBox background={globalTheme.colors.nuetral2} maxWidth='max-content' padding={'12px'} borderRadius={50} borderColor={errors.tower ? globalTheme.colors.red : globalTheme.colors.nuetral2} maxHeight={'max-content'} className={'pointer'} overflow={'visible'}>
                                <ActionMenu btnName="Select Tower"
                                selectedValue={roomData.tower}
                                menuList = {(towersList || []).map((tower) => ({
                                    value: tower.id,
                                    dropmenu: tower.tower,
                                }))}
                                onSelect={(value) =>  handleRoomDataChange("tower", value)}
                                />
                            </WrapperBox>
                            {errors.tower && (
                                <Text font12 inter regular style={{ color: globalTheme.colors.red }}>
                                    {errors.tower}
                                </Text>
                            )}
                        </Flexbox>
                    </Flexbox>
                </WrapperBox>
                <WrapperBox borderRadius={30} borderColor={globalTheme.colors.nuetral2}>
                    <Flexbox ref={fieldRefs.images} spaceBW alignCenter>
                        <Text
                            font16
                            inter
                            medium
                            black4
                            maxWidth="max-content"
                        >
                            Room Photos <span style={{ color: globalTheme.colors.red }}>*</span>
                        </Text>
                        <AccordianIcon />
                    </Flexbox>
                    <Breaker
                        marginTop={16}
                        marginBottom={16}
                        borderColor={globalTheme.colors.nuetral2}
                    />
                    {errors.images && (
                        <Text font12 inter regular className="mb12" style={{ color: globalTheme.colors.red }}>
                            {errors.images}
                        </Text>
                    )}
                    <FileUploadBox
                        onFileSelect={onFileSelect}
                        maxFiles={5}
                        currentCount={roomData.images?.length || 0}
                        onLimitExceeded={(attempted, max) =>
                            toast.error(
                                `You can upload up to ${max} photos per room`,
                                {
                                    toastId: "room-photo-limit",
                                    autoClose: 3000,
                                }
                            )
                        }
                    />
                    {isFileUploading && (
                        <Flexbox gap={8} alignCenter maxWidth="max-content" className="mt12">
                            <ButtonLoader size="16px" color={globalTheme.colors.black3} />
                            <Text font13 inter medium nuetral6>
                                Uploading photo…
                            </Text>
                        </Flexbox>
                    )}
                    <Flexbox gap={16} alignStart className="mt16 respFormRow">
                        {roomData.images?.map((url, index) => (
                            <WrapperBox
                                key={`${url}-${index}`}
                                borderRadius={20}
                                padding={"0"}
                                overflow={"hidden"}
                                maxWidth={"385px"}
                                onClick={() => removeImage(index)}
                            >
                                <img
                                    src={url}
                                    alt={`room-${index}`}
                                    style={{ width: "100%" }}
                                />
                            </WrapperBox>
                        ))}
                    </Flexbox>
                </WrapperBox>
                {/* <WrapperBox borderRadius={30} borderColor={globalTheme.colors.nuetral2}>
                    <Flexbox spaceBW alignCenter>
                        <Text font16 inter medium black4 maxWidth='max-content'>Room Photos</Text>
                        <AccordianIcon />
                    </Flexbox>
                    <Breaker marginTop={16} marginBottom={16} borderColor={globalTheme.colors.nuetral2} />
                    <FileUploadBox />
                    <Flexbox gap={16} alignStart className="mt16 respFormRow">
                        <WrapperBox borderRadius={20} padding={'0'} overflow={'hidden'} maxWidth={'385px'}>
                            <img src='https://filestack-mastersunion.s3.ap-south-1.amazonaws.com/w4cs9PhPSMV07oV2h5pe_WhatsApp%20Image%202025-10-11%20at%205.31.56%20PM.jpeg' style={{ width: '100%' }} />
                        </WrapperBox>
                        <WrapperBox borderRadius={20} padding={'0'} overflow={'hidden'} maxWidth={'385px'}>
                            <img src='https://filestack-mastersunion.s3.ap-south-1.amazonaws.com/w4cs9PhPSMV07oV2h5pe_WhatsApp%20Image%202025-10-11%20at%205.31.56%20PM.jpeg' style={{ width: '100%' }} />
                        </WrapperBox>
                        <WrapperBox borderRadius={20} padding={'0'} overflow={'hidden'} maxWidth={'385px'}>
                            <img src='https://filestack-mastersunion.s3.ap-south-1.amazonaws.com/w4cs9PhPSMV07oV2h5pe_WhatsApp%20Image%202025-10-11%20at%205.31.56%20PM.jpeg' style={{ width: '100%' }} />
                        </WrapperBox>
                        <WrapperBox borderRadius={20} padding={'0'} overflow={'hidden'} maxWidth={'385px'}>
                            <img src='https://filestack-mastersunion.s3.ap-south-1.amazonaws.com/w4cs9PhPSMV07oV2h5pe_WhatsApp%20Image%202025-10-11%20at%205.31.56%20PM.jpeg' style={{ width: '100%' }} />
                        </WrapperBox>
                    </Flexbox>
                </WrapperBox> */}

                <WrapperBox
                    borderRadius={30}
                    borderColor={globalTheme.colors.nuetral2}
                >
                    <Flexbox spaceBW alignCenter>
                        <Flexbox gap={12} alignCenter maxWidth="max-content">
                            <Text font16 inter medium black4>
                                Amenities
                            </Text>
                            <Text font14 inter medium nuetral6>
                                ({selectedAmenities?.length || 0}/
                                {amenityOptions?.length || 0} Amenities
                                selected)
                            </Text>
                        </Flexbox>
                        <AccordianIcon />
                    </Flexbox>
                    <Breaker
                        marginTop={16}
                        marginBottom={16}
                        borderColor={globalTheme.colors.nuetral2}
                    />
                    <Flexbox gap={16} alignStart wrap className="respFormRow">
                        {amenityOptions?.map((amenity) => {
                            const isSelected = selectedAmenities.some(
                                (a) => a.value === amenity.value
                            );
                            return (
                                <WrapperBox
                                    key={amenity.value}
                                    padding="10px 16px"
                                    borderRadius={20}
                                    background={
                                        isSelected
                                            ? globalTheme.colors.nuetral2
                                            : "transparent"
                                    }
                                    borderColor={globalTheme.colors.nuetral2}
                                    maxWidth="max-content"
                                    className="pointer"
                                    onClick={() =>
                                        setSelectedAmenities((prev) =>
                                            prev.some(
                                                (a) => a.value === amenity.value
                                            )
                                                ? prev.filter(
                                                      (a) =>
                                                          a.value !==
                                                          amenity.value
                                                  )
                                                : [...prev, amenity]
                                        )
                                    }
                                >
                                    <Flexbox
                                        alignCenter
                                        gap={8}
                                        maxWidth="max-content"
                                    >
                                        <Text nuetral13 font14 inter regular>
                                            {amenity.name}
                                        </Text>
                                        <span
                                            style={{
                                                display: "inline-flex",
                                                visibility: isSelected
                                                    ? "visible"
                                                    : "hidden",
                                            }}
                                        >
                                            <GreenCheckFilled />
                                        </span>
                                    </Flexbox>
                                </WrapperBox>
                            );
                        })}
                        <Button
                            primaryOutlined
                            name="Add New Amenity"
                            icon={
                                <AddIcon
                                    color={globalTheme.colors.nuetral13}
                                />
                            }
                            revIcon
                            buttonMd
                            height={44}
                            onClick={() => setAddAmenityPopupOpen(true)}
                        />
                    </Flexbox>
                </WrapperBox>
                {isLab && (
                    <WrapperBox
                        borderRadius={30}
                        borderColor={globalTheme.colors.nuetral2}
                    >
                        <Flexbox spaceBW alignCenter>
                            <Flexbox gap={12} alignCenter maxWidth="max-content">
                                <Text font16 inter medium black4>
                                    Equipment
                                </Text>
                                <Text font14 inter medium nuetral6>
                                    ({selectedEquipment?.length || 0}/
                                    {equipmentOptions?.length || 0} Equipment
                                    selected)
                                </Text>
                            </Flexbox>
                            <AccordianIcon />
                        </Flexbox>
                        <Breaker
                            marginTop={16}
                            marginBottom={16}
                            borderColor={globalTheme.colors.nuetral2}
                        />
                        <Flexbox gap={16} alignStart wrap className="respFormRow">
                            {equipmentOptions?.map((equipment) => {
                                const isSelected = selectedEquipment.some(
                                    (e) => e.value === equipment.value
                                );
                                return (
                                    <WrapperBox
                                        key={equipment.value}
                                        padding="10px 16px"
                                        borderRadius={20}
                                        background={
                                            isSelected
                                                ? globalTheme.colors.nuetral2
                                                : "transparent"
                                        }
                                        borderColor={globalTheme.colors.nuetral2}
                                        maxWidth="max-content"
                                        className="pointer"
                                        onClick={() =>
                                            setSelectedEquipment((prev) =>
                                                prev.some(
                                                    (e) => e.value === equipment.value
                                                )
                                                    ? prev.filter(
                                                          (e) =>
                                                              e.value !==
                                                              equipment.value
                                                      )
                                                    : [...prev, equipment]
                                            )
                                        }
                                    >
                                        <Flexbox
                                            alignCenter
                                            gap={8}
                                            maxWidth="max-content"
                                        >
                                            <Text nuetral13 font14 inter regular>
                                                {equipment.name}
                                            </Text>
                                            <span
                                                style={{
                                                    display: "inline-flex",
                                                    visibility: isSelected
                                                        ? "visible"
                                                        : "hidden",
                                                }}
                                            >
                                                <GreenCheckFilled />
                                            </span>
                                        </Flexbox>
                                    </WrapperBox>
                                );
                            })}
                            <Button
                                primaryOutlined
                                name="Add New Equipment"
                                icon={
                                    <AddIcon
                                        color={globalTheme.colors.nuetral13}
                                    />
                                }
                                revIcon
                                buttonMd
                                height={44}
                                onClick={() => setAddEquipmentPopupOpen(true)}
                            />
                        </Flexbox>
                    </WrapperBox>
                )}
                <WrapperBox
                    borderRadius={30}
                    borderColor={globalTheme.colors.nuetral2}
                >
                    <Flexbox spaceBW alignCenter>
                        <Text font16 inter medium black4>
                            Availability Settings
                        </Text>
                        <AccordianIcon />
                    </Flexbox>
                    <Breaker
                        marginTop={16}
                        marginBottom={16}
                        borderColor={globalTheme.colors.nuetral2}
                    />
                    <Flexbox gap={16} column>
                        <Text font14 inter medium nuetral13>
                            Available Days
                        </Text>
                        <Flexbox gap={16} wrap>
                            {WEEK_DAYS.map((day) => {
                                const isSelected =
                                    roomData.availableDay?.includes(day.key);
                                return (
                                    <WrapperBox
                                        key={day.key}
                                        background={
                                            isSelected
                                                ? globalTheme.colors.black3
                                                : "transparent"
                                        }
                                        borderColor={
                                            isSelected
                                                ? globalTheme.colors.black3
                                                : globalTheme.colors.nuetral2
                                        }
                                        borderRadius={50}
                                        padding={"10px 13px"}
                                        maxWidth={"max-content"}
                                        className="pointer"
                                        onClick={() =>
                                            handleRoomDataChange(
                                                "availableDay",
                                                day.key
                                            )
                                        }
                                    >
                                        <Text
                                            font14
                                            inter
                                            medium
                                            white={isSelected}
                                            nuetral6={!isSelected}
                                        >
                                            {day.label}
                                        </Text>
                                    </WrapperBox>
                                );
                            })}
                        </Flexbox>
                        <Flexbox ref={fieldRefs.availability} gap={16} alignStart>
                            <Dropdown
                                label="Available From"
                                placeholder="Select start time"
                                value={TIME_OPTIONS.find((opt) => opt.value === roomData.availableFrom)?.value || ""}
                                options={TIME_OPTIONS}
                                error={errors.availableFrom}
                                onChange={(e) =>
                                    handleRoomDataChange(
                                        "availableFrom",
                                        e?.value || ""
                                    )
                                }
                            />
                            <Dropdown
                                label="Available Till"
                                placeholder="Select end time"
                                value={roomData.availableTo}
                                options={TIME_OPTIONS.filter(
                                    (opt) =>
                                        !roomData.availableFrom ||
                                        opt.value > roomData.availableFrom
                                )}
                                error={errors.availableTo}
                                onChange={(e) =>
                                    handleRoomDataChange(
                                        "availableTo",
                                        e?.value || ""
                                    )
                                }
                            />
                            <InputField
                                label="Max. Slot Duration"
                                isImp
                                // hasIcon={<Clock />}
                                placeholder="30 Minutes"
                                type="number"
                                value={roomData.slotDuration}
                                onChange={(e) =>
                                    handleRoomDataChange(
                                        "slotDuration",
                                        Number(e.target.value)
                                    )
                                }
                                disabled={true}
                            />
                        </Flexbox>
                        <Flexbox
                            background={globalTheme.colors.yellow2}
                            borderColor={globalTheme.colors.yellow}
                            borderRadius={16}
                            padding={"16px 12px"}
                            spaceBW
                            alignCenter
                        >
                            <Flexbox gap={16} maxWidth="max-content">
                                <InfoIcon />
                                <Flexbox column>
                                    <Text font14 inter semiBold black4>
                                        Maintenance Mode
                                    </Text>
                                    <Text font12 inter regular nuetral6>
                                        When enabled, the room will be marked
                                        as unavailable for bookings
                                    </Text>
                                </Flexbox>
                            </Flexbox>
                            <ToggleButton
                                width={36}
                                height={20}
                                toggleSize={16}
                                checked={roomData.status === "maintenance"}
                                onChange={(e) =>
                                    handleRoomDataChange(
                                        "status",
                                        e.target.checked
                                            ? "maintenance"
                                            : "active"
                                    )
                                }
                            />
                        </Flexbox>
                    </Flexbox>
                </WrapperBox>
                <Flexbox gap={16} alignCenter justiCenter>
                    <Button
                        primaryFilled
                        buttonMd
                        minWidth="150px"
                        disabled={isSaving || isFileUploading}
                        cursor={isSaving || isFileUploading ? "not-allowed" : "pointer"}
                        name={
                            isSaving ? (
                                <ButtonLoader size="18px" color="#ffffff" />
                            ) : roomToBeUpdatedDetails ? (
                                "Save Changes"
                            ) : (
                                "Add Room"
                            )
                        }
                        onClick={isSaving || isFileUploading ? undefined : onSave}
                    />
                </Flexbox>
            </Flexbox>
        </>
    );
};

export default EditMeetingRoom;
